import { getDbwTrackingParams } from './get-dbw-tracking-params';
import { getClinicalKeywordSearched } from '../direct-booking/utils';
import { looksLikeInternalBookingUrl } from './url';
import { APPT_OPTIONS_KEYS } from '../profile-v9/lib/constants';
import { FullAppointmentInfo } from 'Src/types';
import { datadogRum } from '@datadog/browser-rum';

/** Appends params to book_online_url and returns the relative url.
 *  @example `https://www.example-pmc-url.com/book/1234?q=test` => `/book/1234?q=test`
 *  Purpose:
 *    1. Move toward allowing client-side transitions to/from directbook
 *    2. Improve SEO
 *    3. Improve dev environment and allow end-to-end testing
 *    4. Handle search-related parameters (coordinates, tracking, etc.)
 * @param  {Object} provider - provider which contains the absolute direct book url and provider id
 * @param  {Object} config - customer service config
 * @param  {{search: string}} location - react-router location object
 * @param  {Object} apptOptions - optional extra key/value params to pass into DBW
 * @param  {Boolean} ignoreApptOptions - optional extra boolean params to pass when true, it will ignore apptOptions to append to the url
 * @param  {Object} searchSummary - optional search context including location coordinates
 * @param  {Boolean} includeDbwTrackingParams - append the user's DBW tracking params (default true).
 *   Pass false for the first client render of server-rendered markup: they are client-only, so the
 *   server omits them and including them during hydration makes the href mismatch.
 * @returns {String} returns a relative direct book url with appended url params from apptOptions
 */
export function getRelativeParameterizedBookingUrl({
  provider,
  config,
  location,
  apptOptions = {},
  ignoreApptOptions = false,
  searchSummary,
  includeDbwTrackingParams = true
}: {
  provider: {
    book_online_url: string | null;
    id: string | number;
  };
  config: any;
  location: {
    pathname: string;
    search: string;
  };
  apptOptions?: FullAppointmentInfo;
  ignoreApptOptions?: boolean;
  searchSummary?: {
    location?: string;
  };
  includeDbwTrackingParams?: boolean;
}): string | null {
  if (provider.book_online_url == null) {
    // book_online_url will be null for providers who are NOT online bookable
    return provider.book_online_url;
  }

  let absoluteBookOnlineUrl: URL;
  try {
    absoluteBookOnlineUrl = new URL(provider.book_online_url);
  } catch (error) {
    const customError = new Error(
      `Provider booking URL is an invalid URL: ${error}`
    );
    datadogRum.addError(customError, {
      bookOnlineUrl: provider.book_online_url,
      providerId: provider.id
    });
    return provider.book_online_url;
  }

  const { category, clinicalKeyword } = getClinicalKeywordSearched(
    location
  ) as any;

  // We only want to append params to the URL if the destination is a domain we control.
  const looksLikeInternalUrl = looksLikeInternalBookingUrl(
    absoluteBookOnlineUrl.pathname
  );

  if (looksLikeInternalUrl) {
    const url = absoluteBookOnlineUrl;

    const allSearchParams: { [key: string]: string | string[] } = {};
    const searchParams = new URLSearchParams(location.search);
    searchParams.forEach((value, key) => {
      if (key in allSearchParams) {
        const currentValue = allSearchParams[key];
        allSearchParams[key] = Array.isArray(currentValue)
          ? [...currentValue, value]
          : [currentValue, value];
      } else {
        allSearchParams[key] = value;
      }
    });

    const paramsToAppend: Record<string, string> = {
      ...(includeDbwTrackingParams && getDbwTrackingParams({ config })),
      ...(clinicalKeyword && { [category]: clinicalKeyword }),
      ...apptOptions,
      ...allSearchParams,
      ...(searchSummary?.location && {
        search_coordinates: searchSummary.location
      })
    };

    Object.entries(paramsToAppend).forEach(
      ([key, val]: [string, string | string[]]) => {
        if (
          ignoreApptOptions &&
          (key === APPT_OPTIONS_KEYS.RELATIONSHIP ||
            key === APPT_OPTIONS_KEYS.PURPOSE)
        ) {
          return;
        }
        if (Array.isArray(val)) {
          val.forEach((v) => {
            url.searchParams.append(key, v);
          });
        } else {
          url.searchParams.append(key, val as string);
        }
      }
    );

    const relativizedBookingUrl = url.href.replace(
      absoluteBookOnlineUrl.origin,
      ''
    );
    return relativizedBookingUrl;
  }

  return provider.book_online_url;
}
